import type { Team } from '@compass/client/types/team';
import type { UserSummary } from '@compass/client/types/user';
import {
  organizationTeamService,
  programService,
  programTeamService,
  userService,
} from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';

// ─── Query Hooks ───────────────────────────────────────────────────────────────

/**
 * Fetches all teams for an organization.
 * Falls back to selectedOrganizationId from the store if not provided.
 */
export const useOrganizationTeams = (organizationId?: string) => {
  const storeOrgId = useStore(state => state.selectedOrganizationId);
  const orgId = organizationId || storeOrgId || '';

  const query = useQuery({
    queryKey: queryKeys.teams.byOrganization(orgId),
    queryFn: () =>
      organizationTeamService.listOrganizationTeams({
        organization_id: orgId,
        limit: 100,
      }),
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    teams: query.data?.teams || [],
  };
};

/**
 * Returns teams assigned to the currently selected program.
 * Uses the program-scoped teams endpoint which only requires program-level permissions.
 */
export const useProgramTeams = () => {
  const storeOrgId = useStore(state => state.selectedOrganizationId);
  const storeProgramId = useStore(state => state.selectedProgramId);
  const orgId = storeOrgId || '';
  const programId = storeProgramId || '';

  const query = useQuery({
    queryKey: queryKeys.teams.byProgram(orgId, programId),
    queryFn: () =>
      programTeamService.listProgramTeams({
        organization_id: orgId,
        program_id: programId,
      }),
    enabled: !!orgId && !!programId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    teams: (query.data?.teams || []) as Team[],
    isLoading: query.isLoading,
    error: query.error,
  };
};

/** LookupUsers accepts at most 100 ids per request (`service/users.go`). */
const LOOKUP_USERS_BATCH_SIZE = 100;

/**
 * Split user ids into LookupUsers-sized batches. Ids are sorted first so the
 * batches (and their cache keys) don't depend on team order.
 *
 * Pure function — exported for unit tests.
 */
export function batchUserIds(ids: Iterable<string>, size = LOOKUP_USERS_BATCH_SIZE): string[][] {
  const sorted = [...new Set(ids)].filter(Boolean).sort();
  const batches: string[][] = [];
  for (let i = 0; i < sorted.length; i += size) batches.push(sorted.slice(i, i + size));
  return batches;
}

/**
 * Given an array of teams, resolves all member_ids and manager_ids into User objects.
 * One LookupUsers call per 100 unique IDs, so many large teams (the Workforce
 * Console selects every eligible team by default) stay under the server cap.
 */
export const useTeamMembersMap = (teams: Team[]) => {
  const userIdBatches = useMemo(() => {
    const ids: string[] = [];
    for (const team of teams) {
      if (team.manager_id) ids.push(team.manager_id);
      if (team.member_ids) ids.push(...team.member_ids);
    }
    return batchUserIds(ids);
  }, [teams]);

  const queries = useQueries({
    queries: userIdBatches.map(batch => ({
      queryKey: ['users', 'byIds', batch],
      queryFn: () => userService.lookupUsers({ user_ids: batch }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    })),
  });

  const usersMap = useStableFold(
    queries,
    q => q.data?.users?.length ?? 0,
    qs => {
      const map = new Map<string, UserSummary>();
      for (const q of qs) {
        for (const user of q.data?.users ?? []) {
          if (user.id) {
            map.set(user.id, {
              id: user.id,
              email: user.email,
              name: user.name,
              available: user.available,
            });
          }
        }
      }
      return map;
    },
  );

  return { usersMap, isLoading: queries.some(q => q.isLoading) };
};

// ─── Mutation Hooks ────────────────────────────────────────────────────────────

export const useCreateOrganizationTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Parameters<typeof organizationTeamService.createOrganizationTeam>[0]) =>
      organizationTeamService.createOrganizationTeam(request),
    onSuccess: (_, variables) => {
      if (variables.organization_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.teams.byOrganization(variables.organization_id),
        });
      }
    },
  });
};

export const useUpdateOrganizationTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Parameters<typeof organizationTeamService.updateOrganizationTeam>[0]) =>
      organizationTeamService.updateOrganizationTeam(request),
    onSuccess: (_, variables) => {
      if (variables.organization_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.teams.byOrganization(variables.organization_id),
        });
      }
      if (variables.organization_id && variables.team_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.teams.byId(variables.organization_id, variables.team_id),
        });
      }
      // Also invalidate individual team query used by useTeamsByIds
      if (variables.team_id) {
        queryClient.invalidateQueries({
          queryKey: ['compass', 'team', variables.team_id],
        });
      }
    },
  });
};

export const useDeleteOrganizationTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Parameters<typeof organizationTeamService.deleteOrganizationTeam>[0]) =>
      organizationTeamService.deleteOrganizationTeam(request),
    onSuccess: (_, variables) => {
      if (variables.organization_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.teams.byOrganization(variables.organization_id),
        });
      }
    },
  });
};

export const useLinkTeamToProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      programId,
      teamId,
    }: {
      organizationId: string;
      programId: string;
      teamId: string;
    }) =>
      programService.updateProgram({
        organization_id: organizationId,
        program_id: programId,
        add_team_ids: [teamId],
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program', variables.programId],
      });
      // Invalidate the programs query used by useOrgAndPrograms (key: ['programs', orgId])
      // so the Zustand store gets refreshed with the updated program.teams array
      queryClient.invalidateQueries({
        queryKey: ['programs', variables.organizationId],
      });
      // Invalidate the program-scoped teams list
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.byProgram(variables.organizationId, variables.programId),
      });
    },
  });
};

export const useUnlinkTeamFromProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      programId,
      teamId,
    }: {
      organizationId: string;
      programId: string;
      teamId: string;
    }) =>
      programService.updateProgram({
        organization_id: organizationId,
        program_id: programId,
        remove_team_ids: [teamId],
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program', variables.programId],
      });
      // Invalidate the programs query used by useOrgAndPrograms (key: ['programs', orgId])
      // so the Zustand store gets refreshed with the updated program.teams array
      queryClient.invalidateQueries({
        queryKey: ['programs', variables.organizationId],
      });
      // Invalidate the program-scoped teams list
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.byProgram(variables.organizationId, variables.programId),
      });
    },
  });
};
